import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { ChevronRight, MoreHorizontal } from 'lucide-react';

import { cn } from '@common/lib/utils';

function BreadcrumbComponent({ ...props }: React.ComponentProps<'nav'>) {
  return <nav aria-label="breadcrumb" data-slot="breadcrumb" {...props} />;
}
const Breadcrumb = React.memo(BreadcrumbComponent);

function BreadcrumbListComponent({ className, ...props }: React.ComponentProps<'ol'>) {
  return (
    <ol
      data-slot="breadcrumb-list"
      className={cn(
        `tw-text-muted-foreground tw-flex tw-flex-wrap tw-items-center tw-gap-1.5 tw-text-sm tw-break-words sm:tw-gap-2.5
        tw-list-none tw-pl-0`,
        className
      )}
      {...props}
    />
  );
}
const BreadcrumbList = React.memo(BreadcrumbListComponent);

function BreadcrumbItemComponent({ className, ...props }: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-item"
      className={cn('tw-inline-flex tw-items-center tw-gap-1.5', className)}
      {...props}
    />
  );
}
const BreadcrumbItem = React.memo(BreadcrumbItemComponent);

function BreadcrumbLinkComponent({
  asChild,
  className,
  ...props
}: React.ComponentProps<'a'> & {
  asChild?: boolean
}) {
  const Comp = asChild ? Slot : 'a';

  return (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <Comp
      data-slot="breadcrumb-link"
      className={cn('hover:tw-text-foreground tw-transition-colors', className)}
      {...(props as any)}
    />
  );
}
const BreadcrumbLink = React.memo(BreadcrumbLinkComponent);

function BreadcrumbPageComponent({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-page"
      role="link"
      aria-disabled="true"
      aria-current="page"
      className={cn('tw-text-foreground tw-font-normal', className)}
      {...props}
    />
  );
}
const BreadcrumbPage = React.memo(BreadcrumbPageComponent);

function BreadcrumbSeparatorComponent({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) {
  return (
    <li
      data-slot="breadcrumb-separator"
      role="presentation"
      aria-hidden="true"
      className={cn('[&>svg]:tw-size-3.5', className)}
      {...props}
    >
      {children ?? <ChevronRight />}
    </li>
  );
}
const BreadcrumbSeparator = React.memo(BreadcrumbSeparatorComponent);

function BreadcrumbEllipsisComponent({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="breadcrumb-ellipsis"
      role="presentation"
      aria-hidden="true"
      className={cn('tw-flex tw-size-9 tw-items-center tw-justify-center', className)}
      {...props}
    >
      <MoreHorizontal className="tw-size-4" />
      <span className="tw-sr-only">More</span>
    </span>
  );
}
const BreadcrumbEllipsis = React.memo(BreadcrumbEllipsisComponent);

export {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
  BreadcrumbEllipsis,
};
